import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import Combobox from '@/components/Combobox.vue';
import DownloadZipButton from '@/components/DownloadZipButton.vue';
import EditDriveItems from '@/views/EditDriveItems.vue';
import Loading from '@/components/Loading.vue';
import UserItem from '@/components/UserItem.vue';
import { GroupUtils } from '@/utils/groupUtils';
import { GraphApiResult } from '@/models/graphApiResult';
import { Users } from '@/models/users';
import { ApiResult } from '@/enums';
import { User } from '@/models/user';
import { Consts } from '@/consts';
import { AppConfig } from '@/app-config';
import { PathUtils } from '@/utils/pathUtils';
import { DriveItems } from '@/models/driveItems';
import { GraphApiUtils } from '@/utils/graphApiUtils';
import { FileUtils } from '@/utils/fileUtils';
import { Plan } from '@/models/plan';
import { PlannerUtils } from '@/utils/plannerUtils';
import { Buckets } from '@/models/buckets';
import { Bucket } from '@/models/bucket';
import { Tasks } from '@/models/tasks';
import { Task } from '@/models/task';
import { Channel } from '@/models/channel';
import { Channels } from '@/models/channels';
import { Plans } from '@/models/plans';

@Component({
	components: {
		Combobox,
		DownloadZipButton,
		Loading,
		UserItem,
		EditDriveItems
	}
})

export default class AdminChannelScore extends Vue {
	@Prop() public channels!: Channels;
	@Prop() public plans!: Plans;

	private groupId: string = '';
	private channel: Channel | null = null;
	private plan: Plan | null = null;

	private subTitle: string = 'レポート採点';
	private buckets: Buckets = new Buckets();
	private users: Users = new Users();
	private bucket: Bucket | null = null;
	private tasks: Tasks = new Tasks();
	private zipDriveItems: DriveItems = new DriveItems();
	private downloadZipButtonEnabled: boolean = false;
	private loading: boolean = true;
	private isVisibleUser: boolean = false;
	private userName: string = '';
	private message: string = '課題が登録されていません';
	private isVisibleMessage: boolean = false;

	// EditDriveItems
	private driveItemsTitle: string = '';
	private driveItemsPath: string = '';
	private isVisibleDriveItems: boolean = false;
	private deleteButtonVisible: boolean = false;
	private uploadButtonVisible: boolean = false;

	public async created(): Promise<void> {
		// アクセストークンが有効でない場合はルートに遷移。
		if (!await GraphApiUtils.isValidAccessToken(Vue.$accessTokenInfo)) {
			this.$router.push(Consts.PATH_ADMIN);
			return;
		}
		await this.update();
	}

	@Watch('channels')
	private async onChannelsChange(newChannels: Channels, oldChannels: Channels): Promise<void> {
		await this.update();
	}

	private isValidTask(tasks: Tasks, planId: string, bucketId: string, taskName: string): boolean {
		const bucketTasks: Tasks = tasks.getBucketTasks(planId, bucketId);
		const task: Task | null = bucketTasks.getByTitle(taskName);
		if ((task === null) || (task.startDateTime === null)) {
			return false;
		}
		return true;
	}

	private async onComboboxSelectChange(bucketId: string): Promise<void> {
		this.downloadZipButtonEnabled = false;
		const bucket: Bucket | null = this.buckets.get(bucketId);
		if (bucket === null) {
			return;
		}
		if ((this.channel === null) || (this.plan === null)) {
			return;
		}
		this.bucket = bucket;
		this.users = new Users();
		this.loading = true;
		if (!this.isValidTask(this.tasks, this.plan.id, bucket.id, AppConfig.ADMIN_TASK_NAME_SCORE_REPORT)) {
			this.isVisibleMessage = true;
			// ファイル一覧を初期化
			this.zipDriveItems = new DriveItems();
		} else {
			this.isVisibleMessage = false;
			const accessToken: string = Vue.$accessTokenInfo.token;
			// ユーザー一覧取得 TODO 本来はユーザー情報はそのままでステータス更新のみできるのでは
			const users: Users | null = await this.getUsers(accessToken, this.groupId);
			if (users === null) {
				return;
			}
			// ユーザーの提出レポート一覧取得
			await this.updateUserReports(accessToken, this.groupId, this.channel.id, bucket.name, users);
			this.users = users;
		}
		this.loading = false;
	}

	private onUserItemButtonClick(userId: string, driveItemsPath: string): void {
		const folderNames: string[] = driveItemsPath.split('/');
		const length: number = folderNames.length;
		if (length === 0) {
			return;
		}
		const user: User | null = this.users.getUser(userId);
		if (user === null) {
			return;
		}
		if (this.bucket === null) {
			return;
		}
		const lastFolderName: string = folderNames[length - 1];
		this.deleteButtonVisible = false;
		this.uploadButtonVisible = false;
		this.isVisibleUser = true;
		this.userName = user.displayName;
		switch (lastFolderName) {
			case AppConfig.USER_TASK_FOLDERNAME_CHECKSCORE: {
				this.driveItemsTitle = `${this.bucket.name} ${AppConfig.USER_TASK_FOLDERNAME_CHECKSCORE}`;
				this.deleteButtonVisible = true;
				this.uploadButtonVisible = true;
				break;
			}
			case AppConfig.USER_TASK_FOLDERNAME_SUBMITREPORT: {
				this.driveItemsTitle = `${this.bucket.name} ${AppConfig.USER_TASK_FOLDERNAME_SUBMITREPORT}`;
				break;
			}
			default: {
				this.driveItemsTitle = '';
				this.isVisibleUser = false;
				this.userName = '';
			}
		}
		this.driveItemsPath = driveItemsPath;
		this.isVisibleDriveItems = true;
	}

	private onDriveItemsClose(): void {
		this.isVisibleDriveItems = false;
	}

	private getZipFileName(channelId: string, bucketName: string): string {
		const shortenName: string | undefined = AppConfig.CHANNEL_ID_TO_SHORTEN_NAME[channelId];
		if (shortenName === undefined) {
			return 'download';
		}
		const fileName: string = `${shortenName}_${bucketName}`;
		return fileName;
	}

	private async update(): Promise<void> {
		// グループ取得
		const groupId: string | null = localStorage.getItem(Consts.LOCAL_STORAGE_KEY_USER_GROUPID);
		if (groupId === null) {
			return;
		}
		this.groupId = groupId;
		// チャンネル取得
		const channelId: string = this.$route.params.id;
		const channel: Channel | null = this.getChannel(channelId);
		if (channel === null) {
			return;
		}
		// プラン取得
		const plan: Plan | null = this.getPlan(channel.displayName);
		if (plan === null) {
			return;
		}
		const accessToken: string = Vue.$accessTokenInfo.token;
		// バケット一覧取得
		const buckets: Buckets | null = await this.getBuckets(accessToken, plan.id);
		if ((buckets === null) || (buckets.value.length === 0)) {
			return;
		}
		// タスク一覧取得
		const tasks: Tasks | null = await this.getTasks(accessToken, plan.id);
		if (tasks === null) {
			return;
		}
		const bucket: Bucket = buckets.value[0];
		this.users = new Users();
		if (!this.isValidTask(tasks, plan.id, bucket.id, AppConfig.ADMIN_TASK_NAME_SCORE_REPORT)) {
			this.isVisibleMessage = true;
		} else {
			this.isVisibleMessage = false;
			// ユーザー一覧の取得
			const users: Users | null = await this.getUsers(accessToken, groupId);
			if (users === null) {
				return;
			}
			// ユーザーの提出レポート一覧取得
			await this.updateUserReports(accessToken, groupId, channelId, bucket.name, users);
			this.users = users;
		}
		this.channel = channel;
		this.plan = plan;
		this.buckets = buckets;
		this.bucket = bucket;
		this.tasks = tasks;
		this.loading = false;
	}

	private getChannel(channelId: string): Channel | null {
		if (this.channels === undefined) {
			return null;
		}
		const channel: Channel | null = this.channels.get(channelId);
		return channel;
	}

	private getPlan(channelName: string): Plan | null {
		if (this.plans === undefined) {
			return null;
		}
		const plan: Plan | null = this.plans.getByTitle(channelName);
		return plan;
	}

	private async getBuckets(accessToken: string, planId: string): Promise<Buckets | null> {
		const bucketsResult = await PlannerUtils.GetBuckets(accessToken, planId);
		if ((bucketsResult.result !== ApiResult.Success) || (bucketsResult.value === null)) {
			alert('バケット一覧の取得に失敗しました');
			return null;
		}
		const buckets: Buckets = bucketsResult.value;
		buckets.ignore(AppConfig.BUCKET_NAME_LECTURE);
		buckets.sort();
		return buckets;
	}

	private async getTasks(accessToken: string, planId: string): Promise<Tasks | null> {
		const tasksResult: GraphApiResult<Tasks> = await PlannerUtils.GetTasks(accessToken, planId);
		if ((tasksResult.result !== ApiResult.Success) || (tasksResult.value === null)) {
			alert('タスクの取得に失敗しました。');
			return null;
		}
		const tasks = tasksResult.value;
		return tasks;
	}

	private async getUsers(accessToken: string, groupId: string): Promise<Users | null> {
		// 任意のチームグループかつ生徒グループに所属するユーザー一覧を取得
		const usersResult = await GroupUtils.GetBothMembers(accessToken, groupId, AppConfig.USER_SECUTIRY_GROUP_ID);
		if ((usersResult.result !== ApiResult.Success) || (usersResult.value === null)) {
			alert('生徒一覧の取得に失敗しました');
			return null;
		}
		const users: Users = usersResult.value;
		return users;
	}

	private async updateUserReports(
		accessToken: string,
		groupId: string,
		channelId: string,
		bucketName: string,
		users: Users
	): Promise<void> {
		const filesForZip: DriveItems = new DriveItems();
		let shortenName: string | undefined = AppConfig.CHANNEL_ID_TO_SHORTEN_NAME[channelId];
		shortenName = (shortenName === undefined) ? '' : `${shortenName}_`;
		for (const user of users.value) {
			const path: string = PathUtils.GetUserSubmitReportPath(user.id, user.displayName, channelId, bucketName);
			const driveItems: DriveItems | null = await this.getDriveItems(accessToken, groupId, path);
			if (driveItems === null) {
				continue;
			}
			const files: DriveItems = driveItems.getFiles();
			const bodyName: string = `${shortenName}${bucketName}_${user.displayName}`;
			// zipダウンロード用に名前をリネームする
			for (const driveItem of files.value) {
				const ext: string = FileUtils.GetExtension(driveItem.name);
				const fileName: string = `${bodyName}.${ext}`;
				const increamentName: string = filesForZip.increamentName(fileName);
				driveItem.name = increamentName;
				filesForZip.value.push(driveItem);
			}
		}
		this.zipDriveItems = filesForZip;
		this.downloadZipButtonEnabled = (this.zipDriveItems.value.length === 0) ? false : true;
	}

	private async getDriveItems(accessToken: string, groupId: string, path: string): Promise<DriveItems | null> {
		const driveItemsResult: GraphApiResult<DriveItems> = await GroupUtils.GetDriveItemsByPath(accessToken, groupId, path);
		if ((driveItemsResult.result !== ApiResult.Success) || (driveItemsResult.value === null)) {
			alert('ファイル一覧の取得に失敗しました');
			return null;
		}
		const driveItems: DriveItems = driveItemsResult.value;
		return driveItems;
	}
}

